import { useEffect, useMemo, useRef, useState } from "react";
import {
  primeMessageBodyCacheFromIndexedDB,
  subscribeMessageBodyCache,
} from "@/lib/cache/messageBodyCache";
import type { MessageBodyCacheStatus } from "@/features/inbox/MessageBodyCacheBadge";
import { INBOX_BODY_STATUS_RETRY_MS, INBOX_CACHE_WINDOW_SIZE } from "@/lib/constants/inbox";
import { reconcileVisibleBodyFlags } from "@/lib/mail/bodyCacheReconcile";
import { MAIL_LIVE_SYNC_DONE_EVENT, type MailLiveSyncDoneDetail } from "@/lib/mail/mailLiveSyncEvents";
import { primeMessageBodyFromServer } from "@/lib/mail/fetchMessageBodyWithCache";
import { isMessageBodyCached } from "@/lib/mail/messageBodyCacheStatus";
import {
  MAIL_NEW_MAIL_EVENT,
  type MailNewMailDetail,
} from "@/lib/mail/mailNewMailEvents";
import {
  MAIL_BODY_WARMED_EVENT,
  type MailBodyWarmedDetail,
} from "@/lib/mail/mailBodyWarmEvents";
import {
  MAIL_BODY_WARMING_EVENT,
  type MailBodyWarmingDetail,
  getWarmingUids,
  resolveMessageBodyCacheStatus,
  subscribeBodyWarmState,
} from "@/lib/mail/mailBodyWarmState";
import {
  getPeriodWarmProgress,
  subscribePeriodWarmProgress,
} from "@/lib/mail/periodWarmProgress";

const PERIOD_WARM_STATUS_MIN_MS = 12_000;

function mergeServerFlags(
  prev: Map<number, boolean>,
  next: Map<number, boolean>
): Map<number, boolean> {
  if (next.size === 0) return prev;
  let changed = false;
  const merged = new Map(prev);
  for (const [uid, value] of next) {
    if (merged.get(uid) !== value) {
      changed = true;
      merged.set(uid, value);
    }
  }
  return changed ? merged : prev;
}

/**
 * Pastilles corps — ready = DD02 seul ; hydrated = DD02 + client ; idle = nuage.
 */
export function useInboxBodyCacheFlags(
  accountId: number | undefined,
  folder: string,
  uids: number[],
  listBodyCached?: ReadonlyMap<number, boolean>,
  /** Non-lus (ou autres) à remonter en priorité depuis IDB si déjà ouverts. */
  priorityUids?: readonly number[]
): Map<number, MessageBodyCacheStatus> {
  const [clientVersion, setClientVersion] = useState(0);
  const [warmVersion, setWarmVersion] = useState(0);
  const [serverFlags, setServerFlags] = useState<Map<number, boolean>>(new Map());
  const uidKey = uids.join(",");
  const priorityKey = priorityUids?.join(",") ?? "";
  const uidsRef = useRef(uids);
  uidsRef.current = uids;
  const priorityRef = useRef<ReadonlySet<number>>(new Set());
  priorityRef.current = new Set(priorityUids?.filter((uid) => uid > 0) ?? []);
  const fetchGen = useRef(0);
  const retryGen = useRef(0);
  const lastWarmBodiesDone = useRef(0);
  const lastPeriodWarmStamp = useRef(0);
  const lastPeriodStatusFetchAt = useRef(0);

  useEffect(() => {
    return subscribeMessageBodyCache(() => setClientVersion((v) => v + 1));
  }, []);

  useEffect(() => {
    return subscribeBodyWarmState(() => setWarmVersion((v) => v + 1));
  }, []);

  const loadServerFlags = useMemo(
    () => async (account: number, fold: string, uidList: number[], force = false) => {
      const windowUids = uidList.slice(0, INBOX_CACHE_WINDOW_SIZE);
      if (windowUids.length === 0) {
        return { ok: true as const, flags: new Map<number, boolean>() };
      }

      const gen = ++fetchGen.current;
      const next = await reconcileVisibleBodyFlags({
        accountId: account,
        folder: fold,
        uids: windowUids,
        force,
      });
      if (gen !== fetchGen.current) {
        return { ok: false as const, flags: null };
      }
      if (!next) {
        return { ok: false as const, flags: null };
      }

      setServerFlags((prev) => mergeServerFlags(prev, next));
      return { ok: true as const, flags: next };
    },
    []
  );

  const scheduleBodyStatusRetries = useMemo(
    () => (account: number, fold: string, uidList: number[]) => {
      const gen = ++retryGen.current;
      for (const delay of INBOX_BODY_STATUS_RETRY_MS) {
        window.setTimeout(() => {
          if (gen !== retryGen.current) return;
          void (async () => {
            const result = await loadServerFlags(account, fold, uidList, true);
            if (!result.ok || !result.flags) return;
            const windowUids = uidList.slice(0, INBOX_CACHE_WINDOW_SIZE);
            const stillMissing = windowUids.some((uid) => !result.flags!.get(uid));
            if (!stillMissing) {
              retryGen.current += 1;
            }
          })();
        }, delay);
      }
    },
    [loadServerFlags]
  );

  const needsBodyStatusRetry = (flags: Map<number, boolean>, uidList: number[]) => {
    const windowUids = uidList.slice(0, INBOX_CACHE_WINDOW_SIZE);
    return windowUids.some((uid) => !flags.get(uid));
  };

  useEffect(() => {
    if (accountId == null || uidKey.length === 0) {
      setServerFlags(new Map());
      return;
    }
    lastWarmBodiesDone.current = 0;
    lastPeriodWarmStamp.current = 0;
    retryGen.current += 1;
    const uidList = uidsRef.current;
    const priority = priorityRef.current;
    let cancelled = false;
    void (async () => {
      // Opportuniste : corps déjà ouverts sur cet appareil (pas de copie masse DD02).
      await primeMessageBodyCacheFromIndexedDB(accountId, folder, uidList, priority);
      if (cancelled) return;
      setClientVersion((v) => v + 1);
      const result = await loadServerFlags(accountId, folder, uidList);
      if (cancelled) return;
      if (result.ok && result.flags && needsBodyStatusRetry(result.flags, uidList)) {
        scheduleBodyStatusRetries(accountId, folder, uidList);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, folder, uidKey, priorityKey, loadServerFlags, scheduleBodyStatusRetries]);

  useEffect(() => {
    if (accountId == null || uidKey.length === 0) return;

    const onNewMail = (event: Event) => {
      const detail = (event as CustomEvent<MailNewMailDetail>).detail;
      if (detail?.accountId !== accountId || detail.folder !== folder || detail.newCount < 1) {
        return;
      }
      const uidList = uidsRef.current;
      void (async () => {
        const result = await loadServerFlags(accountId, folder, uidList, true);
        if (result.ok && result.flags && needsBodyStatusRetry(result.flags, uidList)) {
          scheduleBodyStatusRetries(accountId, folder, uidList);
        }
      })();
    };

    const onBodiesWarming = (event: Event) => {
      const detail = (event as CustomEvent<MailBodyWarmingDetail>).detail;
      if (detail?.accountId !== accountId || detail.folder !== folder) return;
      setWarmVersion((v) => v + 1);
    };

    const onBodiesWarmed = (event: Event) => {
      const detail = (event as CustomEvent<MailBodyWarmedDetail>).detail;
      if (detail?.accountId !== accountId || detail.folder !== folder) return;

      setServerFlags((prev) => {
        const next = new Map(prev);
        for (const [key, ready] of Object.entries(detail.flags)) {
          const uid = Number(key);
          if (uid > 0 && ready) next.set(uid, true);
        }
        return mergeServerFlags(prev, next);
      });
      setWarmVersion((v) => v + 1);
    };

    window.addEventListener(MAIL_NEW_MAIL_EVENT, onNewMail);
    window.addEventListener(MAIL_BODY_WARMING_EVENT, onBodiesWarming);
    window.addEventListener(MAIL_BODY_WARMED_EVENT, onBodiesWarmed);
    return () => {
      window.removeEventListener(MAIL_NEW_MAIL_EVENT, onNewMail);
      window.removeEventListener(MAIL_BODY_WARMING_EVENT, onBodiesWarming);
      window.removeEventListener(MAIL_BODY_WARMED_EVENT, onBodiesWarmed);
    };
  }, [accountId, folder, uidKey, loadServerFlags, scheduleBodyStatusRetries]);

  useEffect(() => {
    if (accountId == null || uidKey.length === 0) return;

    return subscribePeriodWarmProgress(() => {
      const warm = getPeriodWarmProgress(accountId);
      const job = warm.job;
      if (!job) return;

      const status = job.status;
      if (status !== "running" && status !== "paused" && status !== "completed") {
        return;
      }

      const done = job.bodiesDone ?? 0;
      const stamp = job.updatedAt ?? 0;
      const shouldRefresh =
        status === "completed" ||
        done !== lastWarmBodiesDone.current ||
        stamp !== lastPeriodWarmStamp.current;

      if (!shouldRefresh) return;

      const now = Date.now();
      if (now - lastPeriodStatusFetchAt.current < PERIOD_WARM_STATUS_MIN_MS) {
        return;
      }
      lastPeriodStatusFetchAt.current = now;
      lastWarmBodiesDone.current = done;
      lastPeriodWarmStamp.current = stamp;

      const uidList = uidsRef.current;
      void (async () => {
        const result = await loadServerFlags(accountId, folder, uidList, true);
        if (
          result.ok &&
          result.flags &&
          (status === "completed" || needsBodyStatusRetry(result.flags, uidList))
        ) {
          scheduleBodyStatusRetries(accountId, folder, uidList);
        }
      })();
    });
  }, [accountId, folder, uidKey, loadServerFlags, scheduleBodyStatusRetries]);

  useEffect(() => {
    if (accountId == null || uidKey.length === 0) return;

    const refreshIfMissing = (force: boolean) => {
      const uidList = uidsRef.current;
      if (uidList.length === 0) return;
      void (async () => {
        const result = await loadServerFlags(accountId, folder, uidList, force);
        if (result.ok && result.flags && needsBodyStatusRetry(result.flags, uidList)) {
          scheduleBodyStatusRetries(accountId, folder, uidList);
        }
      })();
    };

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      refreshIfMissing(true);
    };

    const onLiveSyncDone = (event: Event) => {
      const detail = (event as CustomEvent<MailLiveSyncDoneDetail>).detail;
      if (detail?.accountId !== accountId) return;
      if (detail.folder && detail.folder !== folder) return;
      refreshIfMissing(true);
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(MAIL_LIVE_SYNC_DONE_EVENT, onLiveSyncDone);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(MAIL_LIVE_SYNC_DONE_EVENT, onLiveSyncDone);
    };
  }, [accountId, folder, uidKey, loadServerFlags, scheduleBodyStatusRetries]);

  return useMemo(() => {
    const map = new Map<number, MessageBodyCacheStatus>();
    if (accountId == null) return map;

    const warming = getWarmingUids(accountId, folder);

    for (const uid of uids) {
      const clientCached = isMessageBodyCached(accountId, folder, uid);
      const warmingNow = warming.has(uid);
      const fromList = listBodyCached?.get(uid) === true;
      const serverCached = serverFlags.get(uid) ?? false;

      map.set(
        uid,
        resolveMessageBodyCacheStatus({
          fromList,
          warmingNow,
          clientCached,
          serverCached,
        })
      );
    }
    return map;
  }, [
    accountId,
    folder,
    uidKey,
    clientVersion,
    warmVersion,
    serverFlags,
    uids,
    listBodyCached,
  ]);
}

/** Précharge un corps au survol (1 UID) — accélère l’ouverture, pas de masse. */
export function prefetchInboxBodyIfCached(
  accountId: number,
  folder: string,
  uid: number,
  status: MessageBodyCacheStatus
): void {
  if (uid <= 0 || status === "idle" || status === "hydrated") return;
  if (isMessageBodyCached(accountId, folder, uid)) return;
  void primeMessageBodyFromServer(accountId, folder, uid, { dd02Only: true });
}
